/**
 * Signal routing advisor — proposes what a Signal Event touches (SIG-4).
 *
 * Canon authority: canon 23 v2 §6.2 (routing may be "proposed by an AI
 * capability and confirmed by a human — proposal-only, P-AGENT-1").
 * Registry: signal_routing_advisor (embedded advisor, doc 22).
 *
 * This module never writes a classification. It returns a proposal the UI
 * renders; the human commit path is classify_signal_event. Disabling the
 * capability removes the proposal, never the classify path.
 */

import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { callFeatureLlm } from './llmClient';
import {
  SIGNAL_ROUTING_CLASSES,
  getSignalOccurrence,
  type SignalRoutingClass,
} from '../types/signal-events';

export interface SignalRoutingProposal {
  routingClass: SignalRoutingClass;
  reason: string;
}

/**
 * Parse and validate the model's JSON reply. Exported for tests.
 * Returns null on anything that is not a clean, valid proposal —
 * a malformed proposal is dropped, never coerced.
 */
export function parseRoutingProposal(text: string | null): SignalRoutingProposal | null {
  if (!text) return null;
  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonMatch[0]);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  const routingClass = obj.routing_class;
  const reason = typeof obj.reason === 'string' ? obj.reason.trim() : '';
  if (
    typeof routingClass !== 'string' ||
    !(SIGNAL_ROUTING_CLASSES as readonly string[]).includes(routingClass) ||
    reason.length === 0
  ) {
    return null;
  }
  return { routingClass: routingClass as SignalRoutingClass, reason };
}

const SYSTEM_PROMPT = `You classify organizational change signals for a prioritization platform.
Given a signal (something that changed), decide which ONE model it touches:

- feasibility_model: what is POSSIBLE changed (capacity, dependency, estimate, constraint, deadline). Default response is replanning, not reprioritization.
- value_model: what is VALUABLE changed (desired outcome, time horizon, risk appetite, competitive or economic ground). The only class that reopens ranking.
- assumption_base: a recorded assumption or expectation RESOLVED, either way (proved right, proved wrong, evidence changed).
- mandate_frame: the FRAME-HOLDERS changed (leadership, org structure, strategy superseded above).
- record_error: the RECORD was wrong at decision time (distinct from the world changing).

Respond with ONLY a JSON object: {"routing_class": "<one of the five>", "reason": "<one short sentence>"}.
If genuinely ambiguous, pick the most likely class and say why in the reason. You are advisory only; a human decides.`;

export async function proposeSignalRouting(input: {
  signalEventId: string;
  occurrence: string;
  message?: string | null;
  severity?: string | null;
  workspaceId?: string | null;
}): Promise<SignalRoutingProposal | null> {
  if (!isAiCapabilityEnabled('signal_routing_advisor', input.workspaceId)) {
    return null;
  }

  const label = getSignalOccurrence(input.occurrence)?.label ?? input.occurrence;
  const userPrompt = [
    `Signal: ${label}`,
    input.message?.trim() ? `Detail: ${input.message.trim()}` : null,
    input.severity ? `Severity: ${input.severity}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  const reply = await callFeatureLlm({
    userPrompt,
    systemPrompt: SYSTEM_PROMPT,
    conversationId: `signal-routing-${input.signalEventId}`,
    maxTokens: 200,
    temperature: 0,
  });

  return parseRoutingProposal(reply);
}
